/**
 * TanStack Query hooks for simulation data fetching and mutations.
 *
 * This module provides:
 * - Queries for fetching simulation data
 * - Mutations for CRUD operations with optimistic updates
 * - Automatic cache invalidation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys, simulationQueryOptions } from '@/lib/queryClient';
import { createDeliverable, createDependency, deleteSpecificDependency, updateSimulation } from '@/lib/api';
import type { SimulationResponse, UpdateSimulationRequest, CreateDeliverableRequest } from '@/types/api';

// ============================================================================
// Queries
// ============================================================================

/**
 * Fetch a simulation by ID with all its data (features, teams, deliverables).
 * When used with route loaders, data will be instantly available from cache.
 */
export function useSimulation(simulationId: string | null | undefined) {
  return useQuery({
    ...simulationQueryOptions(simulationId || ''),
    enabled: !!simulationId,
  });
}

// ============================================================================
// Simulation Mutations
// ============================================================================

/**
 * Mutation for updating a simulation's metadata (name, description, start_date, seed).
 * Uses optimistic updates for instant UI feedback.
 */
export function useUpdateSimulation(simulationId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateSimulationRequest) => {
      // Read ETag from React Query cache (single source of truth)
      const simulation = queryClient.getQueryData<SimulationResponse>(queryKeys.simulation(simulationId!));
      return updateSimulation(simulationId!, data, simulation?.etag);
    },

    onMutate: async (data) => {
      if (!simulationId) return;

      // Note: We intentionally DON'T cancel queries here.
      // SSE-triggered refetches may contain updated ETags that we need.

      const previousSimulation = queryClient.getQueryData<SimulationResponse>(queryKeys.simulation(simulationId));

      if (previousSimulation) {
        // Build update object only for fields that have actual string values
        const update: Partial<SimulationResponse> = {};
        if (data.name !== undefined && data.name !== null) update.name = data.name;
        if (data.description !== undefined) update.description = data.description;
        if (data.start_date !== undefined && data.start_date !== null) update.start_date = data.start_date;
        if (data.seed !== undefined) update.seed = data.seed;

        queryClient.setQueryData<SimulationResponse>(queryKeys.simulation(simulationId), {
          ...previousSimulation,
          ...update,
        });
      }

      return { previousSimulation };
    },

    // Update cache with server response to get new ETag immediately
    onSuccess: (updatedSimulation) => {
      if (!simulationId) return;

      const currentSimulation = queryClient.getQueryData<SimulationResponse>(queryKeys.simulation(simulationId));
      if (currentSimulation) {
        // Merge the updated fields (including new etag) while preserving teams/features
        queryClient.setQueryData<SimulationResponse>(queryKeys.simulation(simulationId), {
          ...currentSimulation,
          name: updatedSimulation.name,
          description: updatedSimulation.description,
          start_date: updatedSimulation.start_date,
          seed: updatedSimulation.seed,
          etag: updatedSimulation.etag,
          updated_at: updatedSimulation.updated_at,
        });
      }
    },

    onError: (_err, _vars, context) => {
      if (context?.previousSimulation && simulationId) {
        queryClient.setQueryData(queryKeys.simulation(simulationId), context.previousSimulation);
      }
    },

    onSettled: () => {
      if (simulationId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.simulation(simulationId) });
      }
    },
  });
}

// ============================================================================
// Deliverable Mutations
// ============================================================================

interface CreateDeliverableVars {
  teamId: string;
  data: CreateDeliverableRequest;
}

/**
 * Mutation for creating a deliverable.
 */
export function useCreateDeliverable(simulationId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, data }: CreateDeliverableVars) => createDeliverable(simulationId!, teamId, data),

    onSuccess: (newDeliverable, { teamId }) => {
      if (!simulationId) return;

      const previousSimulation = queryClient.getQueryData<SimulationResponse>(queryKeys.simulation(simulationId));

      if (previousSimulation) {
        queryClient.setQueryData<SimulationResponse>(queryKeys.simulation(simulationId), {
          ...previousSimulation,
          teams: previousSimulation.teams.map((t) =>
            t.id === teamId ? { ...t, deliverables: [...t.deliverables, newDeliverable] } : t,
          ),
        });
      }
    },

    onSettled: () => {
      if (simulationId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.simulation(simulationId) });
      }
    },
  });
}

// ============================================================================
// Dependency Mutations
// ============================================================================

interface CreateDependencyVars {
  deliverableId: string;
  predecessorId: string;
  teamId: string;
}

/**
 * Mutation for creating a dependency with optimistic update.
 */
export function useCreateDependency(simulationId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deliverableId, predecessorId }: CreateDependencyVars) =>
      createDependency(deliverableId, predecessorId),

    onMutate: async ({ deliverableId, predecessorId, teamId }) => {
      if (!simulationId) return;

      await queryClient.cancelQueries({ queryKey: queryKeys.simulation(simulationId) });

      const previousSimulation = queryClient.getQueryData<SimulationResponse>(queryKeys.simulation(simulationId));

      if (previousSimulation) {
        queryClient.setQueryData<SimulationResponse>(queryKeys.simulation(simulationId), {
          ...previousSimulation,
          teams: previousSimulation.teams.map((t) =>
            t.id === teamId
              ? {
                  ...t,
                  deliverables: t.deliverables.map((d) =>
                    d.id === deliverableId ? { ...d, predecessors: [...d.predecessors, predecessorId] } : d,
                  ),
                }
              : t,
          ),
        });
      }

      return { previousSimulation };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousSimulation && simulationId) {
        queryClient.setQueryData(queryKeys.simulation(simulationId), context.previousSimulation);
      }
    },

    onSettled: () => {
      if (simulationId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.simulation(simulationId) });
      }
    },
  });
}

interface DeleteDependencyVars {
  deliverableId: string;
  predecessorId: string;
  teamId: string;
}

/**
 * Mutation for deleting a dependency with optimistic update.
 */
export function useDeleteDependency(simulationId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deliverableId, predecessorId }: DeleteDependencyVars) =>
      deleteSpecificDependency(deliverableId, predecessorId),

    onMutate: async ({ deliverableId, predecessorId, teamId }) => {
      if (!simulationId) return;

      await queryClient.cancelQueries({ queryKey: queryKeys.simulation(simulationId) });

      const previousSimulation = queryClient.getQueryData<SimulationResponse>(queryKeys.simulation(simulationId));

      if (previousSimulation) {
        queryClient.setQueryData<SimulationResponse>(queryKeys.simulation(simulationId), {
          ...previousSimulation,
          teams: previousSimulation.teams.map((t) =>
            t.id === teamId
              ? {
                  ...t,
                  deliverables: t.deliverables.map((d) =>
                    d.id === deliverableId
                      ? { ...d, predecessors: d.predecessors.filter((p) => p !== predecessorId) }
                      : d,
                  ),
                }
              : t,
          ),
        });
      }

      return { previousSimulation };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousSimulation && simulationId) {
        queryClient.setQueryData(queryKeys.simulation(simulationId), context.previousSimulation);
      }
    },

    onSettled: () => {
      if (simulationId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.simulation(simulationId) });
      }
    },
  });
}

// ============================================================================
// SSE Integration
// ============================================================================

/**
 * Hook to invalidate simulation query when SSE events arrive.
 * Use this in conjunction with useSimulationEvents to keep data fresh.
 * Returns a function that invalidates and refetches the query.
 */
export function useInvalidateSimulation() {
  const queryClient = useQueryClient();

  return async (simulationId: string) => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.simulation(simulationId) });
  };
}
